import { ref, computed } from 'vue';
import AgentsAPI from 'dashboard/api/agents/agents';
import BillingAPI from 'dashboard/api/billing';
import FlexcallsCallAPI from 'dashboard/api/flexcallsCall';

// Module-scoped cache — fetched once per page load, shared across all
// callers, same pattern as useCallDevices.js. We only need `active` +
// `has_flexcalls_call_node` per agent, so reusing the agent list the
// management screens already fetch avoids a purpose-built endpoint.
const agents = ref([]);
let loaded = false;
let pending = null;

export async function loadFlexcallsAgents() {
  if (loaded) return;
  if (pending) {
    await pending;
    return;
  }

  pending = AgentsAPI.get()
    .then(({ data }) => {
      agents.value = Array.isArray(data) ? data : [];
    })
    .catch(() => {
      // Fail silently — agents stays empty; the button won't show. A missing
      // "Call with AI" action beats a button that reliably errors.
    })
    .finally(() => {
      loaded = true;
      pending = null;
    });

  await pending;
}

// Módulo pago `modulo-ligacoes` (R$197/mês, sem grandfather): sem ele o botão
// não pode nem aparecer, senão o clique bate no endpoint gateado e o
// interceptor global do APIHelper abre o paywall na cara de quem só abriu
// uma conversa. `billing/modules` NÃO é gateado (é a própria consulta de
// estado, ver CallDevicesController#inbox_map pro mesmo padrão do WaCalls),
// então é seguro chamar em background. Falha ao buscar = trata como
// desligado (mesmo "fail silently, esconde o botão" das outras caches
// acima) — não dá pra assumir habilitado sem confirmação.
const callingModuleEnabled = ref(false);
let moduleLoaded = false;
let modulePending = null;

export async function loadCallingModuleState() {
  if (moduleLoaded) return;
  if (modulePending) {
    await modulePending;
    return;
  }

  modulePending = BillingAPI.getModules()
    .then(({ data }) => {
      callingModuleEnabled.value = data?.modules?.calling?.enabled === true;
    })
    .catch(() => {
      callingModuleEnabled.value = false;
    })
    .finally(() => {
      moduleLoaded = true;
      modulePending = null;
    });

  await modulePending;
}

export function useFlexcallsCall() {
  // Trigger the fetches on first composable use (no-op on subsequent calls).
  loadFlexcallsAgents();
  loadCallingModuleState();

  // Gate: only render the button when the account has the calling module AND
  // at least one active agent with a `flexcalls_call` node configured —
  // otherwise the operator clicks and hits an error they cannot fix
  // themselves (or, without the module check, a 402 that pops the paywall).
  const hasCallCapableAgent = computed(
    () =>
      callingModuleEnabled.value &&
      agents.value.some(agent => agent.active && agent.has_flexcalls_call_node)
  );

  const placeCall = conversationId => FlexcallsCallAPI.create(conversationId);

  return {
    hasCallCapableAgent,
    placeCall,
  };
}
